import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  ElementNode,
  TextNode,
} from 'lexical';
import { $isListItemNode, $isListNode } from '@lexical/list';
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  Transformer,
} from '@lexical/markdown';
import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
} from '@lexical/table';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { TABLE_ROW_REG_EXP, TABLE_ROW_DIVIDER_REG_EXP } from './config/transformers/table';
import { isHtmlOrigin } from './utils/tableOrigin';

const PLACEHOLDER_PATTERN = /\[(\{ADVISOR_TO_COMPLETE\})]\{\.Placeholder\}/g;
const PLACEHOLDER_REPLACEMENT = '<suggestion source="placeholder">$1</suggestion>';

export function convertPlaceholdersToSuggestionTags(md: string): string {
  return md.replace(PLACEHOLDER_PATTERN, PLACEHOLDER_REPLACEMENT);
}

export const replacePlaceholdersWithImages = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce(
    (acc, img) => acc.replaceAll(`{image:${img.id}}`, `![Image](${img.encodedImage})`),
    md,
  );
};

export const replaceImagesWithPlaceholders = (md: string, imgs: MarkdownImage[]) => {
  return imgs.reduce(
    (acc, img) => acc.replaceAll(`![Image](${img.encodedImage})`, `{image:${img.id}}`),
    md,
  );
};

export function $setEditorValue(markdown: string, transformers: Transformer[]): void {
  const root = $getRoot();
  root.clear();
  $convertFromMarkdownString(markdown, transformers, undefined, true);
}

const OPENING_TAG = '<replacement>';
const CLOSING_TAG = '</replacement>';
const MD_FORMAT_MARKERS = ['***', '**', '*', '___', '__', '_', '~~'];

export function normalizeReplacementMarkers(markdown: string): string {
  let result = markdown;
  for (const marker of MD_FORMAT_MARKERS) {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(
      new RegExp(`${escaped}(${OPENING_TAG})`, 'g'),
      `${OPENING_TAG}${marker}`,
    );
    result = result.replace(
      new RegExp(`(${CLOSING_TAG})${escaped}`, 'g'),
      `${marker}${CLOSING_TAG}`,
    );
  }
  return result;
}

type InsertedMarkers = {
  openingNode: TextNode;
  openingOffset: number;
  closingNode: TextNode;
  closingOffset: number;
};

export function $insertReplacementMarkers(): InsertedMarkers | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
  const endPoint = selection.isBackward() ? selection.anchor : selection.focus;

  const selectedNodes = selection.getNodes();
  if (selectedNodes.length === 0) return null;

  const firstTextNode = selectedNodes.find((n): n is TextNode => $isTextNode(n));
  const lastTextNode = [...selectedNodes].reverse().find((n): n is TextNode => $isTextNode(n));

  if (!firstTextNode || !lastTextNode) return null;

  const startOffset =
    firstTextNode.getKey() === startPoint.getNode().getKey() ? startPoint.offset : 0;
  const endOffset =
    lastTextNode.getKey() === endPoint.getNode().getKey()
      ? endPoint.offset
      : lastTextNode.getTextContentSize();

  if (firstTextNode === lastTextNode) {
    firstTextNode.spliceText(startOffset, 0, OPENING_TAG, true);
    firstTextNode.spliceText(endOffset + OPENING_TAG.length, 0, CLOSING_TAG, true);
    return {
      openingNode: firstTextNode,
      openingOffset: startOffset,
      closingNode: firstTextNode,
      closingOffset: endOffset + OPENING_TAG.length,
    };
  } else {
    lastTextNode.spliceText(endOffset, 0, CLOSING_TAG, true);
    firstTextNode.spliceText(startOffset, 0, OPENING_TAG, true);
    return {
      openingNode: firstTextNode,
      openingOffset: startOffset,
      closingNode: lastTextNode,
      closingOffset: endOffset,
    };
  }
}

export function $removeReplacementMarkers(markers: InsertedMarkers): void {
  const { openingNode, openingOffset, closingNode, closingOffset } = markers;
  closingNode.spliceText(closingOffset, CLOSING_TAG.length, '', true);
  openingNode.spliceText(openingOffset, OPENING_TAG.length, '', true);
}

export function $removeEmptyTableRows(): void {
  const root = $getRoot();
  for (const child of root.getChildren()) {
    if (!$isTableNode(child)) continue;

    const rows = child.getChildren().filter($isTableRowNode);
    for (const row of rows) {
      const cells = row.getChildren().filter($isTableCellNode);
      if (cells.length > 0 && cells.every(cell => cell.getTextContent().trim() === '')) {
        row.remove();
      }
    }

    if (child.getChildrenSize() === 0) {
      child.remove();
    }
  }
}

export function $removeEmptyTableColumns(): void {
  const root = $getRoot();
  for (const child of root.getChildren()) {
    if (!$isTableNode(child)) continue;

    const rows = child.getChildren().filter($isTableRowNode);
    if (rows.length === 0) continue;

    const grid = rows.map(row => row.getChildren().filter($isTableCellNode));
    const columnCount = grid[0]?.length ?? 0;

    for (let col = columnCount - 1; col >= 0; col--) {
      const allEmpty = grid.every(
        cells => col >= cells.length || cells[col].getTextContent().trim() === '',
      );

      if (allEmpty) {
        for (const cells of grid) {
          if (col < cells.length) {
            cells[col].remove();
          }
        }
      }
    }

    if (child.getChildrenSize() === 0 || rows.every(r => r.getChildrenSize() === 0)) {
      child.remove();
    }
  }
}

export function $removeEmptyListItems(root: ElementNode): void {
  const walk = (node: ElementNode) => {
    for (const child of node.getChildren()) {
      if (!$isElementNode(child)) continue;
      walk(child);
      if ($isListItemNode(child) && child.getTextContent().trim() === '') {
        child.remove();
      } else if ($isListNode(child) && child.getChildrenSize() === 0) {
        child.remove();
      }
    }
  };
  walk(root);
}

const DELETION_TAG_PREFIX = '<suggestion source="deletion">';
const LIST_PREFIX_PATTERN = /^\s*(?:[-*+]\s+|\d+\.\s+)?/;
const HEADING_PATTERN = /^#{1,6}\s+/;

function allChildrenAreDeletions(
  lines: string[],
  startIndex: number,
  isChildLine: (line: string) => boolean,
): { hasChildren: boolean; allDeleted: boolean } {
  let hasChildren = false;
  for (let j = startIndex; j < lines.length; j++) {
    const childLine = lines[j];
    if (childLine.trim() === '') continue;
    if (!isChildLine(childLine)) break;
    hasChildren = true;
    const stripped = childLine.replace(LIST_PREFIX_PATTERN, '');
    if (!stripped.startsWith(DELETION_TAG_PREFIX)) {
      return { hasChildren: true, allDeleted: false };
    }
  }
  return { hasChildren, allDeleted: true };
}

export function markOrphanParents(markdown: string): string {
  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes('<suggestion')) continue;

    const m = line.match(/^(\s*(?:[-*+]\s+|\d+\.\s+)|#{1,6}\s+|>\s+)/);
    if (!m) continue;

    const next = lines[i + 1];
    if (!next) continue;

    const nextStripped = next.replace(LIST_PREFIX_PATTERN, '');
    if (!nextStripped.startsWith(DELETION_TAG_PREFIX)) continue;

    const isBullet = /^\s*[-*+]\s+/.test(line);
    const isHeading = HEADING_PATTERN.test(line);

    if (isBullet) {
      const parentIndent = line.match(/^\s*/)![0].length;
      const nextIndent = next.match(/^\s*/)![0].length;
      if (nextIndent <= parentIndent) continue;

      const result = allChildrenAreDeletions(lines, i + 1, childLine => {
        const childIndent = childLine.match(/^\s*/)![0].length;
        return childIndent > parentIndent;
      });
      if (!result.allDeleted) continue;
    } else if (isHeading) {
      const result = allChildrenAreDeletions(
        lines,
        i + 1,
        childLine => !HEADING_PATTERN.test(childLine),
      );
      if (!result.hasChildren || !result.allDeleted) continue;
    }

    const prefix = m[1];
    lines[i] = `${prefix}<suggestion source="deletion">${line.slice(prefix.length)}</suggestion>`;
  }
  return lines.join('\n');
}

export function normalizeInlineSuggestions(markdown: string): string {
  return markdown.replace(
    /<suggestion(?: source="([^"]*)")?>([\s\S]*?)<\/suggestion>/g,
    (_match, source, content) => {
      const sourceAttr = source ? ` source="${source}"` : '';

      const lines = content.trim().split('\n');
      const isTableRows = lines.every((line: string) => TABLE_ROW_REG_EXP.test(line.trim()));

      if (isTableRows) {
        return lines
          .map((line: string) => {
            const trimmed = line.trim();
            if (TABLE_ROW_DIVIDER_REG_EXP.test(trimmed)) return trimmed;
            const inner = trimmed.slice(1, trimmed.lastIndexOf('|'));
            const cells = inner.split('|');
            const wrapped = cells.map((cell: string) => {
              const text = cell.trim();
              return ` <suggestion${sourceAttr}>${text || '\u200b'}</suggestion> `;
            });
            return `|${wrapped.join('|')}|`;
          })
          .join('\n');
      }

      const normalizedContent = content.replace(/\n/g, '<br/>');
      return `<suggestion${sourceAttr}>${normalizedContent}</suggestion>`;
    },
  );
}

export function $isPartialColumnSelection(info: {
  tableNodeKey: string;
  startCol: number;
  endCol: number;
}): boolean {
  const tableNode = $getNodeByKey(info.tableNodeKey);
  if (!tableNode || !$isTableNode(tableNode)) return false;
  const totalCols = tableNode.getColumnCount();
  return info.startCol !== 0 || info.endCol < totalCols - 1;
}

function findPipeTableBlocks(lines: string[]): { start: number; end: number }[] {
  const blocks: { start: number; end: number }[] = [];
  let i = 0;
  while (i < lines.length) {
    if (TABLE_ROW_REG_EXP.test(lines[i])) {
      const start = i;
      i++;
      while (
        i < lines.length &&
        (TABLE_ROW_REG_EXP.test(lines[i]) || TABLE_ROW_DIVIDER_REG_EXP.test(lines[i]))
      ) {
        i++;
      }
      blocks.push({ start, end: i - 1 });
    } else {
      i++;
    }
  }
  return blocks;
}

export function $getTableSelectionMarkdown(
  tableNodeKey: string,
  startRow: number,
  endRow: number,
  transformers: Transformer[],
): string | null {
  const tableNode = $getNodeByKey(tableNodeKey);
  if (!tableNode || !$isTableNode(tableNode) || isHtmlOrigin(tableNode)) return null;

  const rows = tableNode.getChildren().filter($isTableRowNode);
  if (startRow < 0 || endRow >= rows.length || startRow > endRow) return null;

  const root = $getRoot();
  let tableOrdinal = -1;
  let count = 0;
  for (const child of root.getChildren()) {
    if ($isTableNode(child) && !isHtmlOrigin(child)) {
      if (child.getKey() === tableNodeKey) {
        tableOrdinal = count;
        break;
      }
      count++;
    }
  }
  if (tableOrdinal === -1) return null;

  const markdown = $convertToMarkdownString(transformers, root);
  const lines = markdown.split('\n');

  const tableBlocks = findPipeTableBlocks(lines);
  if (tableOrdinal >= tableBlocks.length) return null;

  const block = tableBlocks[tableOrdinal];
  const tableStartLine = block.start;

  const firstRow = rows[0];
  const hasHeaderRow = firstRow
    .getChildren()
    .some(cell => $isTableCellNode(cell) && cell.__headerState === TableCellHeaderStates.ROW);

  const rowToLine = (rowIdx: number): number => {
    if (!hasHeaderRow) return tableStartLine + rowIdx;
    return tableStartLine + (rowIdx === 0 ? 0 : rowIdx + 1);
  };

  const firstLine = rowToLine(startRow);
  const lastLine = rowToLine(endRow);
  if (firstLine >= lines.length || lastLine >= lines.length) return null;

  lines[firstLine] = OPENING_TAG + lines[firstLine];
  lines[lastLine] = lines[lastLine] + CLOSING_TAG;

  return lines.join('\n');
}
